import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { adminAPI } from '../lib/api';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users, MoreHorizontal, Edit, Trash2, Shield, ShieldCheck, Loader2, Key,
  User, AlertTriangle, Wrench, Check, X, Plus, Crown, UserPlus, Globe, CheckSquare,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const ROLE_CONFIG = {
  superuser: { label: 'Superuser', icon: Crown, bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  admin: { label: 'Admin', icon: ShieldCheck, bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  staff: { label: 'Staff', icon: User, bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
};

const formatLastSeen = (isoString) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 2) return 'Online now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const RoleBadge = ({ role }) => {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.staff;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.bg} ${cfg.text} ${cfg.border}`} data-testid={`role-badge-${role}`}>
      <Icon className="w-3 h-3 mr-1" /> {cfg.label}
    </Badge>
  );
};

export const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [engineerAccounts, setEngineerAccounts] = useState([]);
  const [portalAccounts, setPortalAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('staff');

  // Dialog states
  const [createDialog, setCreateDialog] = useState({ open: false, type: '' });
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [resetPwDialog, setResetPwDialog] = useState({ open: false, target: null, accountType: '' });

  // Form states
  const [formData, setFormData] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  // Portal bulk selection
  const [selectedPortalIds, setSelectedPortalIds] = useState(new Set());
  const [bulkPortalDialog, setBulkPortalDialog] = useState({ open: false, action: '' });
  const [bulkPortalPassword, setBulkPortalPassword] = useState('');

  const myRole = currentUser?.role || (currentUser?.is_admin ? 'admin' : 'staff');
  const isSuperuser = myRole === 'superuser';

  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminAPI.getUsers();
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    }
  }, []);

  const fetchEngineers = useCallback(async () => {
    try {
      const res = await adminAPI.getEngineerAccounts();
      setEngineerAccounts(res.data || []);
    } catch { /* silent */ }
  }, []);

  const fetchPortalAccounts = useCallback(async () => {
    try {
      const res = await adminAPI.getPortalAccounts();
      setPortalAccounts(res.data || []);
    } catch { /* silent */ }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchEngineers(), fetchPortalAccounts(), fetchCustomers()]);
      setLoading(false);
    };
    load();
  }, [fetchUsers, fetchEngineers, fetchPortalAccounts, fetchCustomers]);

  // Filtered user lists
  const staffUsers = users.filter(u => u.role === 'staff' || (!u.role && !u.is_admin));
  const adminUsers = users.filter(u => u.role === 'admin' || u.role === 'superuser' || (!u.role && u.is_admin));

  // --- Create handlers ---
  const openCreateDialog = (type) => {
    const defaults = {
      user: { name: '', email: '', password: '', role: 'staff' },
      engineer: { name: '', email: '', password: '', phone: '', date_of_birth: '', address: '', role: 'engineer' },
      portal: { customer_id: '', email: '', password: '' },
    };
    setFormData(defaults[type] || {});
    setCreateDialog({ open: true, type });
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      if (createDialog.type === 'user') {
        await adminAPI.createUser(formData);
        toast.success(`${formData.role === 'staff' ? 'Staff' : formData.role === 'admin' ? 'Admin' : 'Superuser'} account created`);
        fetchUsers();
      } else if (createDialog.type === 'engineer') {
        await adminAPI.createEngineer(formData);
        toast.success('Engineer account created');
        fetchEngineers();
      } else if (createDialog.type === 'portal') {
        await adminAPI.createPortalAccount(formData);
        toast.success('Customer portal account created');
        fetchPortalAccounts();
      }
      setCreateDialog({ open: false, type: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  // --- Edit handler ---
  const openEditDialog = (user) => {
    setEditDialog({ open: true, user });
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role || 'staff',
      ena_sub_installer_username: user.ena_sub_installer_username || '',
      email_notifications: user.email_notifications !== false,
    });
  };

  const handleSave = async () => {
    if (!editDialog.user) return;
    setSaving(true);
    try {
      const data = { name: formData.name, email: formData.email, role: formData.role };
      if (formData.password) data.password = formData.password;
      if (formData.ena_sub_installer_username !== undefined) {
        data.ena_sub_installer_username = formData.ena_sub_installer_username;
      }
      data.email_notifications = formData.email_notifications !== false;
      await adminAPI.updateUser(editDialog.user.id, data);
      toast.success('User updated');
      setEditDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // --- Delete handler ---
  const handleDelete = async () => {
    if (!deleteDialog.user) return;
    try {
      await adminAPI.deleteUser(deleteDialog.user.id);
      toast.success('User deleted');
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // --- Password reset handler ---
  const openResetPw = (target, accountType) => {
    setResetPwDialog({ open: true, target, accountType });
    setNewPassword('');
  };

  const handleResetPassword = async () => {
    if (!resetPwDialog.target || !newPassword) return;
    setSaving(true);
    try {
      const { target, accountType } = resetPwDialog;
      if (accountType === 'user') {
        await adminAPI.resetUserPassword(target.id, { new_password: newPassword });
      } else if (accountType === 'engineer') {
        await adminAPI.resetEngineerPassword(target.id, { new_password: newPassword });
      } else if (accountType === 'portal') {
        await adminAPI.resetPortalPassword(target.id, { new_password: newPassword });
      }
      toast.success('Password reset successfully');
      setResetPwDialog({ open: false, target: null, accountType: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  // --- Portal bulk actions ---
  const togglePortalSelect = (id) => {
    setSelectedPortalIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const togglePortalSelectAll = () => {
    if (selectedPortalIds.size === portalAccounts.length) {
      setSelectedPortalIds(new Set());
    } else {
      setSelectedPortalIds(new Set(portalAccounts.map(a => a.id)));
    }
  };

  const handleBulkPortalAction = async () => {
    const { action } = bulkPortalDialog;
    setSaving(true);
    try {
      const data = { account_ids: Array.from(selectedPortalIds), action };
      if (action === 'reset_password') data.new_password = bulkPortalPassword;
      const res = await adminAPI.bulkPortalAction(data);
      toast.success(res.data.message);
      setBulkPortalDialog({ open: false, action: '' });
      setBulkPortalPassword('');
      setSelectedPortalIds(new Set());
      fetchPortalAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  // --- Engineer approve/reject ---
  const handleApproveEngineer = async (id) => {
    try {
      await adminAPI.approveEngineer(id);
      toast.success('Engineer approved');
      fetchEngineers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectEngineer = async (id) => {
    try {
      await adminAPI.rejectEngineer(id);
      toast.success('Engineer deactivated');
      fetchEngineers();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  // --- Shared user table row ---
  const UserRow = ({ user, canResetPw = true }) => {
    const targetRole = user.role || (user.is_admin ? 'admin' : 'staff');
    const canEdit = isSuperuser || targetRole !== 'superuser';
    const canReset = canResetPw && (isSuperuser || targetRole !== 'superuser');
    const lastSeen = formatLastSeen(user.last_seen_at);
    const isOnline = lastSeen === 'Online now';
    return (
      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
        <TableCell>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                {user.name.charAt(0).toUpperCase()}
              </div>
              {isOnline && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" data-testid={`online-indicator-${user.id}`} />
              )}
            </div>
            <div>
              <p className="font-medium text-slate-900">{user.name}</p>
              {user.id === currentUser?.id && <span className="text-xs text-blue-600">(You)</span>}
            </div>
          </div>
        </TableCell>
        <TableCell className="text-slate-600">{user.email}</TableCell>
        <TableCell><RoleBadge role={targetRole} /></TableCell>
        <TableCell>
          <Badge className={user.active !== false ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}>
            {user.active !== false ? 'Active' : 'Inactive'}
          </Badge>
        </TableCell>
        <TableCell data-testid={`last-seen-${user.id}`}>
          {lastSeen ? (
            <div className="flex items-center gap-1.5">
              <Clock className={`w-3.5 h-3.5 ${isOnline ? 'text-emerald-500' : 'text-slate-400'}`} />
              <span className={`text-sm ${isOnline ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>{lastSeen}</span>
            </div>
          ) : (
            <span className="text-sm text-slate-400">Never</span>
          )}
        </TableCell>
        <TableCell className="text-sm text-slate-500">{formatDate(user.created_at)}</TableCell>
        <TableCell>
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`}>
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
                {canReset && (
                  <DropdownMenuItem onClick={() => openResetPw(user, 'user')} data-testid={`reset-pw-${user.id}`}>
                    <Key className="w-4 h-4 mr-2" /> Reset Password
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDeleteDialog({ open: true, user })}
                  className="text-red-600"
                  disabled={user.id === currentUser?.id}
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const UsersTable = ({ userList, emptyMsg }) => (
    userList.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Users className="w-12 h-12 mb-3 opacity-50" />
        <p className="text-sm">{emptyMsg}</p>
      </div>
    ) : (
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="font-semibold">User</TableHead>
            <TableHead className="font-semibold">Email</TableHead>
            <TableHead className="font-semibold">Role</TableHead>
            <TableHead className="font-semibold">Status</TableHead>
            <TableHead className="font-semibold">Last Seen</TableHead>
            <TableHead className="font-semibold">Created</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {userList.map(u => <UserRow key={u.id} user={u} />)}
        </TableBody>
      </Table>
    )
  );

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">User Management</h1>
            <p className="text-slate-500 mt-1">Create and manage all account types</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-account-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Account
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => openCreateDialog('user')} data-testid="create-staff-btn">
                <User className="w-4 h-4 mr-2" /> Staff / Admin Account
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openCreateDialog('engineer')} data-testid="create-engineer-btn">
                <Wrench className="w-4 h-4 mr-2" /> Engineer Account
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openCreateDialog('portal')} data-testid="create-portal-btn">
                <Globe className="w-4 h-4 mr-2" /> Customer Portal Account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="staff" data-testid="tab-staff">Staff ({staffUsers.length})</TabsTrigger>
            <TabsTrigger value="admins" data-testid="tab-admins">Admins ({adminUsers.length})</TabsTrigger>
            <TabsTrigger value="engineers" data-testid="tab-engineers">
              Engineers ({engineerAccounts.length})
              {engineerAccounts.filter(e => !e.approved && e.active).length > 0 && (
                <span className="ml-1.5 w-5 h-5 inline-flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full">
                  {engineerAccounts.filter(e => !e.approved && e.active).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="portal" data-testid="tab-portal">Customer Portal ({portalAccounts.length})</TabsTrigger>
          </TabsList>

          {/* Staff Tab */}
          <TabsContent value="staff">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-500" /> Staff Users
                </CardTitle>
                <CardDescription>Regular CRM users with standard access</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <UsersTable userList={staffUsers} emptyMsg="No staff users" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-purple-500" /> Admins & Superusers
                </CardTitle>
                <CardDescription>Users with elevated permissions. Only superusers can manage other superusers.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <UsersTable userList={adminUsers} emptyMsg="No admin users" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Engineers Tab */}
          <TabsContent value="engineers">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-amber-500" /> Engineer Accounts
                </CardTitle>
                <CardDescription>Engineers access the portal at /engineer-login</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {engineerAccounts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Wrench className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-sm">No engineer accounts</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-semibold">Name</TableHead>
                        <TableHead className="font-semibold">Email</TableHead>
                        <TableHead className="font-semibold">Phone</TableHead>
                        <TableHead className="font-semibold">Role</TableHead>
                        <TableHead className="font-semibold">Status</TableHead>
                        <TableHead className="font-semibold">Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {engineerAccounts.map((acc) => (
                        <TableRow key={acc.id} data-testid={`engineer-row-${acc.id}`}>
                          <TableCell className="font-medium">{acc.name}</TableCell>
                          <TableCell className="text-slate-600">{acc.email}</TableCell>
                          <TableCell className="text-slate-600">{acc.phone || '-'}</TableCell>
                          <TableCell>
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              <Wrench className="w-3 h-3 mr-1" /> {acc.role || 'engineer'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {acc.approved ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Approved</Badge>
                            ) : acc.active ? (
                              <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                            ) : (
                              <Badge variant="secondary">Rejected</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">{formatDate(acc.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {!acc.approved && acc.active && (
                                <>
                                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApproveEngineer(acc.id)} data-testid={`approve-eng-${acc.id}`}>
                                    <Check className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" variant="destructive" onClick={() => handleRejectEngineer(acc.id)} data-testid={`reject-eng-${acc.id}`}>
                                    <X className="w-3 h-3 mr-1" /> Reject
                                  </Button>
                                </>
                              )}
                              {acc.approved && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openResetPw(acc, 'engineer')}>
                                      <Key className="w-4 h-4 mr-2" /> Reset Password
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="text-red-600" onClick={() => handleRejectEngineer(acc.id)}>
                                      <Trash2 className="w-4 h-4 mr-2" /> Deactivate
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customer Portal Tab */}
          <TabsContent value="portal">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                      <Globe className="w-5 h-5 text-teal-500" /> Customer Portal Accounts
                    </CardTitle>
                    <CardDescription>Customer accounts for the self-service portal</CardDescription>
                  </div>
                </div>
                {selectedPortalIds.size > 0 && (
                  <div className="flex items-center gap-3 p-3 mt-3 bg-teal-50 border border-teal-200 rounded-lg" data-testid="portal-bulk-bar">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="w-4 h-4 text-teal-600" />
                      <span className="text-sm font-medium text-teal-900">{selectedPortalIds.size} selected</span>
                    </div>
                    <div className="h-5 w-px bg-teal-200" />
                    <Button size="sm" variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-100" onClick={() => { setBulkPortalPassword(''); setBulkPortalDialog({ open: true, action: 'reset_password' }); }} data-testid="portal-bulk-reset-btn">
                      <Key className="w-3.5 h-3.5 mr-1.5" /> Reset Passwords
                    </Button>
                    <Button size="sm" variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-100" onClick={() => setBulkPortalDialog({ open: true, action: 'activate' })} data-testid="portal-bulk-activate-btn">
                      <Check className="w-3.5 h-3.5 mr-1.5" /> Activate
                    </Button>
                    <Button size="sm" variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-100" onClick={() => setBulkPortalDialog({ open: true, action: 'deactivate' })} data-testid="portal-bulk-deactivate-btn">
                      <X className="w-3.5 h-3.5 mr-1.5" /> Deactivate
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100" onClick={() => setBulkPortalDialog({ open: true, action: 'delete' })} data-testid="portal-bulk-delete-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                    </Button>
                    <div className="ml-auto">
                      <Button size="sm" variant="ghost" className="text-teal-600" onClick={() => setSelectedPortalIds(new Set())}>
                        <X className="w-3.5 h-3.5 mr-1" /> Clear
                      </Button>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {portalAccounts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Globe className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-sm">No portal accounts</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-[40px]">
                          <input
                            type="checkbox"
                            checked={selectedPortalIds.size === portalAccounts.length && portalAccounts.length > 0}
                            onChange={togglePortalSelectAll}
                            className="rounded border-slate-300"
                            data-testid="portal-select-all"
                          />
                        </TableHead>
                        <TableHead className="font-semibold">Customer</TableHead>
                        <TableHead className="font-semibold">Email</TableHead>
                        <TableHead className="font-semibold">Status</TableHead>
                        <TableHead className="font-semibold">Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {portalAccounts.map((acc) => (
                        <TableRow key={acc.id} className={selectedPortalIds.has(acc.id) ? 'bg-teal-50' : ''} data-testid={`portal-row-${acc.id}`}>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedPortalIds.has(acc.id)}
                              onChange={() => togglePortalSelect(acc.id)}
                              className="rounded border-slate-300"
                              data-testid={`portal-select-${acc.id}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{acc.customer_name || acc.name || '-'}</TableCell>
                          <TableCell className="text-slate-600">{acc.email}</TableCell>
                          <TableCell>
                            <Badge className={acc.active !== false ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}>
                              {acc.active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">{formatDate(acc.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openResetPw(acc, 'portal')}>
                                  <Key className="w-4 h-4 mr-2" /> Reset Password
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ===== Create Account Dialog ===== */}
        <Dialog open={createDialog.open} onOpenChange={(open) => !open && setCreateDialog({ open: false, type: '' })}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                {createDialog.type === 'user' && 'Create Staff / Admin Account'}
                {createDialog.type === 'engineer' && 'Create Engineer Account'}
                {createDialog.type === 'portal' && 'Create Customer Portal Account'}
              </DialogTitle>
              <DialogDescription>
                {createDialog.type === 'user' && 'Create a new CRM user account with a specific role.'}
                {createDialog.type === 'engineer' && 'Create a pre-approved engineer account for the engineer portal.'}
                {createDialog.type === 'portal' && 'Create a portal login for an existing customer.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* User form */}
              {createDialog.type === 'user' && (
                <>
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Chloe Smith" data-testid="create-user-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="email@helioses.co.uk" data-testid="create-user-email" />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input type="password" value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="Min 6 characters" data-testid="create-user-password" />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={formData.role || 'staff'} onValueChange={v => setFormData({...formData, role: v})}>
                      <SelectTrigger data-testid="create-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        {isSuperuser && <SelectItem value="superuser">Superuser</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Engineer form */}
              {createDialog.type === 'engineer' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. James Wilson" data-testid="create-eng-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="email@example.com" data-testid="create-eng-email" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input type="password" value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="Min 6 characters" data-testid="create-eng-password" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Contact Number</Label>
                      <Input value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="07XXX XXXXXX" data-testid="create-eng-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input type="date" value={formData.date_of_birth || ''} onChange={e => setFormData({...formData, date_of_birth: e.target.value})} data-testid="create-eng-dob" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Full address" data-testid="create-eng-address" />
                  </div>
                  <div className="space-y-2">
                    <Label>Role / Specialisation</Label>
                    <Select value={formData.role || 'engineer'} onValueChange={v => setFormData({...formData, role: v})}>
                      <SelectTrigger data-testid="create-eng-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="engineer">Engineer</SelectItem>
                        <SelectItem value="installer">Installer</SelectItem>
                        <SelectItem value="electrician">Electrician</SelectItem>
                        <SelectItem value="surveyor">Surveyor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Portal form */}
              {createDialog.type === 'portal' && (
                <>
                  <div className="space-y-2">
                    <Label>Select Customer</Label>
                    <Select value={formData.customer_id || ''} onValueChange={v => {
                      const cust = customers.find(c => c.id === v);
                      setFormData({...formData, customer_id: v, email: cust?.email || formData.email });
                    }}>
                      <SelectTrigger data-testid="create-portal-customer">
                        <SelectValue placeholder="Choose a customer..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Portal Email</Label>
                    <Input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="customer@email.com" data-testid="create-portal-email" />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input type="password" value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="Min 6 characters" data-testid="create-portal-password" />
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialog({ open: false, type: '' })}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-account-submit">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Edit User Dialog ===== */}
        <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, user: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user details. Leave password blank to keep current.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} data-testid="edit-user-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} data-testid="edit-user-email" />
              </div>
              <div className="space-y-2">
                <Label>New Password (optional)</Label>
                <Input type="password" value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="Leave blank to keep" data-testid="edit-user-password" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role || 'staff'} onValueChange={v => setFormData({...formData, role: v})}>
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    {isSuperuser && <SelectItem value="superuser">Superuser</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ENA Sub-Installer Username (optional)</Label>
                <Input
                  value={formData.ena_sub_installer_username || ''}
                  onChange={e => setFormData({...formData, ena_sub_installer_username: e.target.value})}
                  placeholder={`Defaults to ${(formData.email || '').split('@')[0] || 'email username'}`}
                  data-testid="edit-user-ena-username"
                />
                <p className="text-[11px] text-slate-500">
                  Override the username sent to ENA Connect Direct on DNO submissions. Leave blank to auto-derive from email.
                  Allowed characters: letters, numbers, dot, dash, underscore.
                </p>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <Label className="text-sm">Notification emails</Label>
                  <p className="text-[11px] text-slate-500">When off, this user still gets in-app notifications but no notification emails.</p>
                </div>
                <Switch
                  checked={formData.email_notifications !== false}
                  onCheckedChange={(v) => setFormData({ ...formData, email_notifications: v })}
                  data-testid="edit-user-email-notifications"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, user: null })}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-user">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Reset Password Dialog ===== */}
        <Dialog open={resetPwDialog.open} onOpenChange={(open) => !open && setResetPwDialog({ open: false, target: null, accountType: '' })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" /> Reset Password
              </DialogTitle>
              <DialogDescription>
                Set a new password for <span className="font-medium">{resetPwDialog.target?.name || resetPwDialog.target?.email}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="reset-pw-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPwDialog({ open: false, target: null, accountType: '' })}>Cancel</Button>
              <Button onClick={handleResetPassword} disabled={saving || !newPassword} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="reset-pw-submit">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Reset Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Delete Dialog ===== */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" /> Delete User
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <span className="font-medium">{deleteDialog.user?.name}</span>? This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ===== Bulk Portal Action Dialog ===== */}
        <Dialog open={bulkPortalDialog.open} onOpenChange={(open) => !saving && setBulkPortalDialog({ open, action: bulkPortalDialog.action })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {bulkPortalDialog.action === 'delete' && <><AlertTriangle className="w-5 h-5 text-red-500" /> Delete Portal Accounts</>}
                {bulkPortalDialog.action === 'activate' && <><Check className="w-5 h-5 text-emerald-500" /> Activate Portal Accounts</>}
                {bulkPortalDialog.action === 'deactivate' && <><X className="w-5 h-5 text-amber-500" /> Deactivate Portal Accounts</>}
                {bulkPortalDialog.action === 'reset_password' && <><Key className="w-5 h-5" /> Reset Passwords</>}
              </DialogTitle>
              <DialogDescription>
                This will affect {selectedPortalIds.size} portal account(s).
                {bulkPortalDialog.action === 'delete' && ' This cannot be undone.'}
              </DialogDescription>
            </DialogHeader>
            {bulkPortalDialog.action === 'reset_password' && (
              <div className="space-y-2 py-2">
                <Label>New Password (same for all selected)</Label>
                <Input type="password" value={bulkPortalPassword} onChange={e => setBulkPortalPassword(e.target.value)} placeholder="Min 6 characters" data-testid="bulk-portal-password" />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkPortalDialog({ open: false, action: '' })} disabled={saving}>Cancel</Button>
              <Button
                onClick={handleBulkPortalAction}
                disabled={saving || (bulkPortalDialog.action === 'reset_password' && !bulkPortalPassword)}
                className={bulkPortalDialog.action === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                data-testid="bulk-portal-confirm"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
